import { useEffect, useRef, useState } from 'react';
import { captureApiError } from '../core/instrumentation/captureApiError';
import { useAppContext } from '../core/context';
import { useApi } from '../platform/api/useApi';
import { useNavigation } from '../platform/navigation/useNavigation';
import { useAppOpener } from '../platform/app-opener/useAppOpener';
import { useIframe } from '../platform/iframe/useIframe';
import { useDevice } from '../platform/device/useDevice';
import { Routes } from '../Routes';
import { viewEvents, interactionEvents, errorReasons } from '../core/services/mixpanelEvents';
import Loader from '../lib/components/Loader';
import { useClaims } from '../core/util/useClaims';
import { shouldCrashAfterSubmit } from '../core/util/devOverrides';
import {
  Countries,
  type MSISDN,
  msisdnToString,
  parseMsisdn,
  prettyPrintMsisdn,
} from '../core/util/msisdn';
import { type PhoneNumberSource } from '../core/util/phoneNumberSource';
import { type SubmitResponse } from '../core/api/submit';
import { type MerchantDetails } from '../core/api/merchant';
import { getDefaultCountry } from '../core/context/claims';
import rememberMeStorage from '../lib/clients/remember-me-storage';
import {
  retrieveIdentity,
  persistIdentity,
  clearIdentity,
} from '../lib/identity/crossDomainIdentity';
import { useLanguageContext } from '../core/locale/LanguageContext';
import { useFlag } from '../feature-flags';
import InputViewLegacy from './InputViewLegacy';
import InputViewRedesigned from './InputViewRedesigned';

const InputView = (): React.ReactNode => {
  const context = useAppContext();
  const apis = useApi();
  const navigation = useNavigation();
  const appOpener = useAppOpener();
  const iframe = useIframe();
  const device = useDevice();
  const languageContext = useLanguageContext();
  const showNewDesign = useFlag('redesign');
  const shouldShowFancyDeeplinkButton = useFlag('fancyDeeplink');
  const rememberMeEnabled = useFlag('rememberMeEnabled');
  const isQrEligible = useFlag('showQr');
  const hasAttemptedAutoOpenRef = useRef(false);

  const [loading, setLoading] = useState(false);
  const claimsLoading = useClaims();
  const [merchant, setMerchant] = useState<MerchantDetails | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [crashAfterSubmit, setCrashAfterSubmit] = useState(false);
  const viewLoadedRef = useRef(false);
  const [skippyAlreadyClicked, setSkippyAlreadyClicked] = useState(false);

  if (!rememberMeEnabled) {
    rememberMeStorage.clear();
  }
  const [remembered, setRemembered] = useState<{ country?: Countries; number?: string }>(() =>
    rememberMeEnabled && !iframe.isEmbedded() ? rememberMeStorage.get() : {}
  );
  const widgetContinueMode = new URLSearchParams(window.location.search).get('widget-continue');
  const [continueAsDismissed, setContinueAsDismissed] = useState(widgetContinueMode === 'new');
  const defaultCountry = remembered.country ?? getDefaultCountry(context.claims);

  const initialMsisdn = parseMsisdn(defaultCountry, context.claims?.mobile || remembered.number);
  // Claims and the remembered number both arrive after mount, so the country cannot be seeded once
  // from state — it is derived, and only a pick in the selector overrides it.
  const [selectedCountry, setSelectedCountry] = useState<Countries | null>(null);
  const country = selectedCountry ?? initialMsisdn.country.country;
  const initialNumberSource: PhoneNumberSource =
    context.claims?.mobile ? 'prefill'
    : remembered.number ? 'remembered'
    : 'manual';

  const rememberedMsisdn =
    remembered.number ? parseMsisdn(defaultCountry, remembered.number) : null;
  const showContinueAs =
    iframe.isEmbedded() && Boolean(rememberedMsisdn?.number) && !continueAsDismissed;
  const displayNumber = rememberedMsisdn ? prettyPrintMsisdn(rememberedMsisdn) : '';

  const autoSubmitting = widgetContinueMode === 'auto' && Boolean(rememberedMsisdn?.number);
  const autoSubmittedRef = useRef(false);

  const applyRemembered = (
    res: Awaited<ReturnType<typeof retrieveIdentity>>,
    phase: 'eager' | 'gesture'
  ) => {
    if (res.accessRequested) {
      context.mixpanel?.trackInteraction(
        res.accessGranted ?
          interactionEvents.STORAGE_ACCESS_GRANTED
        : interactionEvents.STORAGE_ACCESS_DENIED
      );
    }
    if (res.number) {
      setRemembered({ country: res.country, number: res.number });
      context.mixpanel?.trackInteraction(interactionEvents.CROSS_DOMAIN_REMEMBER_ME_PREFILLED, {
        Source: res.source,
        Phase: phase,
      });
    }
  };

  useEffect(() => {
    if (!iframe.isEmbedded() || !rememberMeEnabled) {
      return;
    }
    let cancelled = false;
    void retrieveIdentity({ phase: 'eager' }).then((res) => {
      if (!cancelled) {
        applyRemembered(res, 'eager');
      }
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once on mount
  }, []);

  const openCustomSchemeLinkFromAutoOpen = () => {
    if (
      !device.isAndroid ||
      hasAttemptedAutoOpenRef.current ||
      iframe.isEmbedded() ||
      device.isWebView
    ) {
      return;
    }
    hasAttemptedAutoOpenRef.current = true;

    try {
      const token = navigation.getCurrentUrl().searchParams.get('token') ?? '';
      context?.mixpanel?.trackInteraction(
        interactionEvents.LANDING_PAGE_CUSTOM_SCHEME_LINK_AUTO_OPENED
      );
      appOpener.openCustomScheme(token, '_self');
    } catch {
      console.warn('CAUGHT not able to open custom scheme url');
    }
  };

  useEffect(() => {
    if (!claimsLoading && context.claims && context.claims.shouldCancelOnBackNav) {
      // Listener intentionally outlives this view — the cancel-on-back behaviour must
      // fire from later routes too (e.g. status). Don't return the teardown.
      navigation.onPopState(async (event) => {
        if ((event.state as { page?: string } | null)?.page === 'abort') {
          await apis.cancel(context);
          navigation.back();
          setTimeout(() => {
            if (context.claims?.fallback) navigation.assign(context.claims.fallback);
          }, 500);
        }
      });
    }
  }, [claimsLoading]);

  useEffect(() => {
    if (!claimsLoading && !merchant && context.claims) {
      apis
        .getMerchantDetails(context)
        .then((merchantDetails) => {
          if (!merchantDetails) {
            context.setRoute(Routes.ERROR, errorReasons.MERCHANT_MISSING);
            return;
          }

          setMerchant(merchantDetails);
          context.mixpanel.setMerchantSerialNumber(merchantDetails.msn);
        })
        .catch((error: unknown) => {
          captureApiError('merchant', error);
          context.setRoute(Routes.ERROR, errorReasons.MERCHANT_FETCH_FAILED);
        });
    }

    // Track view load only after merchant data is loaded so msn is available on first view event.
    if (
      !claimsLoading &&
      context.claims &&
      merchant &&
      isQrEligible !== null &&
      !viewLoadedRef.current
    ) {
      viewLoadedRef.current = true;
      context.mixpanel?.trackViewLoad(viewEvents.LANDING_PAGE_VIEWED, {
        'No Edit Mode': context.claims.noEdit,
        Amount: context.claims.amount,
        Currency: context.claims.currency,
        'QR Enabled': isQrEligible,
        'Block Phone Number Input': context.claims.blockPhoneNumberInput,
      });
      openCustomSchemeLinkFromAutoOpen();
    }
  }, [claimsLoading, context, isQrEligible, merchant]);

  const onSubmitSuccess = (
    submitResponse: SubmitResponse,
    rememberMe: boolean,
    numberSource: PhoneNumberSource
  ) => {
    if (rememberMe && submitResponse.phoneNumber) {
      void persistIdentity(country, submitResponse.phoneNumber).then((res) => {
        context.mixpanel?.trackInteraction(interactionEvents.CROSS_DOMAIN_REMEMBER_ME_PERSISTED, {
          Durable: res.durable,
          'Phone Number Country': country,
        });
      });
    } else {
      void clearIdentity();
    }

    if (context.claims) {
      context.mixpanel?.trackInteraction(interactionEvents.LANDING_PAGE_PHONENUMBER_SUBMITTED, {
        'Remember Me Used': rememberMe,
        'Phone Number Country': country,
        'Number Source': numberSource,
      });
    }

    context.setSubmitResponse(submitResponse);
    context.setRoute(Routes.STATUS);
  };

  const onSubmitError = async (error: unknown) => {
    if (error instanceof Response && error.status === 400) {
      setLoading(false);
      try {
        const v = await error.json();
        setSubmitError(v && v.message ? v.message : languageContext.getLocalizedText('inputError'));
      } catch {
        setSubmitError(languageContext.getLocalizedText('inputError'));
        context.mixpanel?.trackError(viewEvents.LANDING_PAGE_VIEWED, `Submit error`);
      }
    } else {
      captureApiError('submit', error);
      context.setRoute(Routes.ERROR, errorReasons.SUBMIT_FAILED);
    }
  };

  const submit = async (m: MSISDN, rememberMe: boolean, numberSource: PhoneNumberSource) => {
    if (!context.claims) {
      return;
    }

    if (context.claims.shouldCancelOnBackNav) {
      const currentUrl = navigation.getCurrentUrl();
      const curPath = currentUrl.pathname + currentUrl.search;
      navigation.replaceState({ page: 'abort' }, '/abort');
      navigation.pushState({ page: 'current' }, curPath);
    }

    setLoading(true);
    try {
      const res = await apis.submitNumber(context, msisdnToString(m));
      // Crashing in render, not here: an async throw skips the error boundary entirely.
      if (shouldCrashAfterSubmit()) {
        setCrashAfterSubmit(true);
        return;
      }
      onSubmitSuccess(res, rememberMe, numberSource);
    } catch (err) {
      void onSubmitError(err);
    }
  };

  const onContinueAs = () => {
    if (rememberedMsisdn) {
      void submit(rememberedMsisdn, true, 'remembered');
    }
  };

  const onUseAnotherNumber = () => setContinueAsDismissed(true);

  useEffect(() => {
    if (autoSubmittedRef.current || !autoSubmitting || claimsLoading || !context.claims) {
      return;
    }
    if (rememberedMsisdn?.number) {
      autoSubmittedRef.current = true;
      // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional one-shot submit
      void submit(rememberedMsisdn, true, 'remembered');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fire once when claims + number are ready
  }, [autoSubmitting, claimsLoading, context.claims, rememberedMsisdn?.number]);

  if (crashAfterSubmit) {
    throw new Error('crash test: exploded after submit');
  }

  if (!context.claims || loading || claimsLoading || !merchant || autoSubmitting) {
    return (
      <div className="loader-container">
        <Loader />
      </div>
    );
  }

  const isNoEdit = context.claims.noEdit;
  // A submit failure replaces the standing no-edit notice.
  const error =
    submitError ?? (isNoEdit ? languageContext.getLocalizedText('inputErrorNoEdit') : null);
  const shouldShowPaymentDetails = !(showNewDesign && iframe.isWidgetSdkEmbed());

  const childProps = {
    merchant,
    claims: context.claims,
    isQrEligible,
    isNoEdit,
    shouldShowPaymentDetails,
    initialMsisdn,
    error,
    loading,
    locale: languageContext.locale,
    shouldShowFancyDeeplinkButton,
    skippyAlreadyClicked,
    onSkippyClick: () => setSkippyAlreadyClicked(true),
    onSubmit: submit,
    onUpdateCountry: setSelectedCountry,
    initialRememberMe: Boolean(remembered.number),
    initialNumberSource,
    showContinueAs,
    displayNumber,
    onContinueAs,
    onUseAnotherNumber,
  };

  return showNewDesign ?
      <InputViewRedesigned {...childProps} />
    : <InputViewLegacy {...childProps} />;
};

export default InputView;
